import { Injectable } from '@angular/core';
import {OverlayCollection} from "./OverlayCollection";
import {CesiumAdapterService} from "../cesiumAdapter21/cesium-adapter.service";
import {Overlay} from "./Overlay";
import {BehaviorSubject, Observable, Subject, throwError} from "rxjs";
import {OverlayCollectionFactory} from "./OverlayCollectionFactory";
import {HttpClient, HttpErrorResponse, HttpHeaders, HttpParams} from "@angular/common/http";
import {MapPosition} from "../../models/MapPosition";
import {catchError} from "rxjs/operators";
import {HttpServiceService} from "../httpService/http-service.service";
import {error} from "util";
import {OverlayType2Collection} from "./OverlayType2Collection";
import {OverlayType2} from "./OverlayType2";
declare var Yiit;

@Injectable({
  providedIn: 'root'
})
export class OverlayService {

  /**
   * List of overlays for management
   */
  protected overlays:OverlayCollection;
  protected overlaysType2:OverlayType2Collection;

  /**
   * Subject for component
   */
  public overlaySubject:BehaviorSubject<Overlay[]> = new BehaviorSubject<Overlay[]>([]);
  public overlayType2Subject:BehaviorSubject<OverlayType2[]> = new BehaviorSubject<OverlayType2[]>([]);

  public overlayTabs:any = {};

  public availableOverlayServices = {
    '1': {
      name: Yiit.t('Satellite+'),
      image: 'https://studio.oneplanevents.com/images/canvas/wsatellite.png',
      description: '<p>' + Yiit.t('High resolution satellite imagery. Select imagery taken from anywhere in the world at different times.') + '</p>',
      isOverlayOfSameType: function (overlay: Overlay) { return overlay.getImage().indexOf('shadowbreak') >= 0; },
      initialConfig: {
        image: 'shadowbreak'
      },
      price: 5,
      hasPreview: true,
      preloadingHTML: '<img src="assets/images/animated-satellite.gif">' +
        '<div class="es-preloading-message">' +
        Yiit.t('Scanning satellites.. This can take up to 5 mins') +
        '</div>',
      errorMessage: Yiit.t('Sorry the satellites needed are out of range right now, please try again shortly')
    },
    '2': {
      name: Yiit.t('Aerial'),
      image: 'https://studio.oneplanevents.com/images/canvas/waerial.png',
      description: '<p>' + Yiit.t('High resolution imagery taken from lower level (drone or planes) to provide a high degree of detail and accuracy. Imagery taken up to 6 times per year. Coverage currently for Australia, New Zealand and North America.') + '</p>',
      isOverlayOfSameType: function (overlay: Overlay) { return overlay.getImage().indexOf('nearmap') >= 0; },
      initialConfig: {
        image: 'nearmaps'
      },
      checkAvailabilityInLocation: function (address, location: MapPosition) {
        return (
          address['country'] == 'AU'
          // || address['country'] == 'NZ'
          || address['country'] == 'US'
        );
      },
      price: 5,
      hasPreview: true,
      preloadingHTML: '<img src="assets/images/loader.gif">' +
        '<div class="es-preloading-message">' +
        Yiit.t('Loading preview... This can take up to 5 mins') +
        '</div>'
    },
    '3': {
      name: Yiit.t('Government'),
      image: 'https://studio.oneplanevents.com/images/canvas/wgovt.png',
      description: '<p>' + Yiit.t('Government mapping services. Currently only UK via Ordnance Survey datahub.') + '</p>',
      isOverlayOfSameType: function (overlay: Overlay) { return overlay.getImage().indexOf('osmap') >= 0; },
      initialConfig: {
        image: 'osmaps'
      },
      checkAvailabilityInLocation: function (address, location: MapPosition) {
        return address['country'] == 'GB';
      },
      price: function (areaInSqrKm: number) {
        return Math.max(5, 15 * areaInSqrKm)
      },
      hasPreview: true,
      preloadingHTML: '<img src="assets/images/loader.gif">' +
        '<div class="es-preloading-message">' +
        Yiit.t('Getting data from Government... This can take up to 5 mins') +
        '</div>'
    }
  };
  public activeOverlayReset$: Subject<void> = new Subject();

  private visibleOverlays:any[] = [];
  private _changedInVisibleOverlays$ = new Subject<{[key: number]: boolean}>();
  private overlayUndoSubject: Subject<void> = new Subject<void>();
  private layoutsUnsavedChanges: number[] = [];

  get changedInVisibleOverlays$(){
    return this._changedInVisibleOverlays$.asObservable();
  }

  constructor(
    public view: CesiumAdapterService,
    private overlayCollectionFactory: OverlayCollectionFactory,
    private http: HttpServiceService
  ) {
    this.overlays = overlayCollectionFactory.get();
    this.overlaysType2 = new OverlayType2Collection(view, http);
    this.initializeFromGlobalConfig();

    this.view.adapterInitialized.subscribe((isLoaded)=>{
      if (isLoaded){ this.draw(); }
    });
  }

  public getOverlayUndo(): Observable<void> {
    return this.overlayUndoSubject.asObservable();
  }

  public sendOverlayUndo(): void {
    this.overlayUndoSubject.next();
  }

  public getOverlaysOfType(key) {
    let r = [];
    this.overlays.forEach((overlay: Overlay) => {
      if (this.availableOverlayServices[key].isOverlayOfSameType(overlay)) {
        r.push(overlay);
      }
    });
    return r;
  }

  public hasOverlayOfType(key) {
    return this.getOverlaysOfType(key).length > 0;
  }

  public addImageOverlay(overlay: any, eventId: number, name: string, encodedFile: any) {
    const csrfFrontend = (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement).content;
    const body = {
      lt_lat: overlay.left_top_lat,
      lt_lng: overlay.left_top_lon,
      rb_lat: overlay.right_bottom_lat,
      rb_lng: overlay.right_bottom_lon,
      opacity: overlay.opacity,
      name,
      encoded_file: encodedFile,
      '_csrf-frontend': csrfFrontend,
    };

    return new Promise((resolve, reject) => {
      this.http.sendPostRequest(
        '/api/overlay/floorplan/create/' + eventId,
        body,
        new HttpHeaders({'Content-Type': 'application/json'})
      ).subscribe((result) => {
          resolve(result);
        },
        (error) => {
          console.error(error);
          reject(error);
        }
      );
    });
  }

  public updateImageOverlay(overlay: Overlay, eventId: number) {
    const csrfFrontend = (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement).content;
    const body = {
      lt_lat: overlay.left_top_lat,
      lt_lng: overlay.left_top_lon,
      rb_lat: overlay.right_bottom_lat,
      rb_lng: overlay.right_bottom_lon,
      opacity: overlay.opacity,
      rotation: overlay.getRotation(),
      floorplan_id: overlay.getId(),
      name: overlay.name,
      '_csrf-frontend': csrfFrontend,
    };

    return new Promise((resolve, reject) => {
      this.http.sendPostRequest(
        '/api/overlay/floorplan/update/' + eventId,
        body,
        new HttpHeaders({'Content-Type': 'application/json'})
      ).subscribe((result) => {
          resolve(result);
        },
        (error) => {
          console.error(error);
          reject(error);
        }
      );
    });
  }

  public deleteImageOverlay(overlayId: number, eventId: number) {
    const csrfFrontend = (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement).content;
    const body = {
      floorplan_id: overlayId,
      '_csrf-frontend': csrfFrontend,
    };

    return new Promise((resolve, reject) => {
      this.http.sendPostRequest(
        '/api/overlay/floorplan/delete/' + eventId,
        body,
        new HttpHeaders({'Content-Type': 'application/json'})
      ).subscribe((result) => {
          resolve(result);
        },
        (error) => {
          console.error(error);
          reject(error);
        }
      );
    });
  }

  public addOverlay(type, name, coordinates?, typeModifier?) {
    let config = Object.assign({}, this.availableOverlayServices[type].initialConfig, {
      name: name,
      status: Overlay.STATUS_PENDING
    });
    if (coordinates) {
      config.left_top_lat = coordinates[0];
      config.left_top_lon = coordinates[1];
      config.right_bottom_lat = coordinates[2];
      config.right_bottom_lon = coordinates[3];
    }
    if (typeModifier) {
      config.image+= ':' + typeModifier;
    }

    return new Promise((resolve, reject) => {
      this.http.sendPostRequest(
        '/api/add-overlay/' + window['EVENT_ID'],
        new HttpParams()
          .set('config', JSON.stringify(config))
          .toString(),
          new HttpHeaders({
            'Content-Type':  'application/x-www-form-urlencoded'
          })
      ).subscribe((result) => {
        if (result['id']) {
          this.overlays.add(result);
          this.overlaySubject.next(this.overlays.asArray());
          resolve(result);
        } else {
          alert(Yiit.t("Can't save overlay"));
        }
      },
        (error) =>{
          reject(error);
        }
      );
    });
  }

  public addOverlayWithoutServerUpdate(config){
    this.overlays.add(config);
    this.overlaySubject.next(this.overlays.asArray());
  }

  public previewOverlay(type, coordinates) {
    let config = Object.assign({}, this.availableOverlayServices[type].initialConfig);
    if (coordinates) {
      config.left_top_lat = coordinates[0];
      config.left_top_lon = coordinates[1];
      config.right_bottom_lat = coordinates[2];
      config.right_bottom_lon = coordinates[3];
    }

    return new Promise((resolve, reject) => {
      this.http.sendPostRequest(
        '/api/preview-overlay/' + window['EVENT_ID'],
        new HttpParams()
          .set('config', JSON.stringify(config))
          .toString(),
          new HttpHeaders({
            'Content-Type':  'application/x-www-form-urlencoded'
          })
      ).subscribe((result) => {
        resolve(result);
      },
      (error) => {
        console.error(error);
        reject(error);
        }
      );
    });
  }

  public setOverlaysGrayScale(val){
    var that = this;
    that.overlays.forEach(function (item:Overlay) {
      item.forEachEntity((id) => {
        if (val == 0){
          that.view.setOverlayGrayscale(item.imageUrl, id);
        }
        else{
          that.view.setOverlayColored(item.imageUrl, id);
        }
      });
    })
  }

  protected initializeFromGlobalConfig() {
    if (window['OVERLAYS']) {
      window['OVERLAYS'].forEach((overlayConfig) => {
        this.overlays.add(overlayConfig);
      });
    }
    this.overlaySubject.next(this.overlays.asArray());

    if (window['OVERLAYS_TYPE2']) {
      window['OVERLAYS_TYPE2'].forEach((overlayConfig) => {
        this.overlaysType2.add(overlayConfig);
      });
    }
    this.overlayType2Subject.next(this.overlaysType2.asArray());
  }

  protected draw() {
    this.overlays.drawAll();
  }

  public getFloorLayouts(){
    return new Observable((observer)=>{
      this.overlaySubject.subscribe((list:Overlay[])=>{
        let result = list.filter((item)=>{
          return item.isFloorLayout()
        });
        observer.next(result || []);
      })
    });
  }

  public getAutoImportPendingLayouts(): Observable<Overlay[]> {
    return new Observable((observer)=>{
      this.overlaySubject.subscribe((list:Overlay[])=>{
        let result = list.filter((item)=>{
          return item.status === 'auto-import-pending';
        });
        observer.next(result || []);
      })
    });
  }

  private isActiveLayout(item:Overlay){
    return item.status == 'active';
  }

  public getOverlayImages(): Observable<any[]> {
    return new Observable((observer) => {
      this.overlaySubject.subscribe((list: Overlay[]) => {
        const result = list.filter((item) => {
          return !item.isFloorLayout() && this.isActiveLayout(item);
        });
        observer.next(result || []);
      });
    });
  }

  public getOverlaysType2(): Observable<any[]> {
    return new Observable((observer) => {
      this.overlayType2Subject.subscribe((list: OverlayType2[]) => {
        observer.next(list || []);
      });
    });
  }

  hasUnsavedLayoutChanges(): boolean {
    return this.layoutsUnsavedChanges.length !== 0;
  }

  clearUnsavedLayoutChanges(): void {
    this.layoutsUnsavedChanges = [];
  }

  markAsLayoutUnsaved(overlayId: number): void {
    if (this.layoutsUnsavedChanges.includes(overlayId)) {
      // Only need to add it once
      return;
    }

    this.layoutsUnsavedChanges.push(overlayId);
  }

  unmarkAsLayoutUnsaved(overlayId: number): void {
    this.layoutsUnsavedChanges = this.layoutsUnsavedChanges.filter(
      (unsaved: number) => unsaved !== overlayId
    );
  }

  private isInVisibleOverlays(item:Overlay){
    for (let i of this.visibleOverlays){
      if (i.id == item.getId()) {
        return true
      }
    }
    return false
  }

  private getIndexInVisibleOverlays(item:Overlay){
    // @ts-ignore
    for (let [index, i] of this.visibleOverlays.entries()){
      if (i.getId() == item.getId()) {
        return index;
      }
    }
    return -1;
  }

  addToVisibleLayouts(item:Overlay){
    if (!this.isInVisibleOverlays(item)){
      this.visibleOverlays.push(item);
      this.view.addToVisibleOverlays(item);
    }
  }

  removeFromVisibleLayouts(item:Overlay){
    if (this.isInVisibleOverlays(item)){
      this.visibleOverlays.splice(this.getIndexInVisibleOverlays(item), 1);
      this.view.removeFromVisibleOverlays(item);
    }
  }

  deleteOverlay(item: Overlay) {
    item.hide();
    this.removeFromVisibleLayouts(item);
    this.overlays.remove(item.getId());
    this.overlaySubject.next(this.overlays.asArray());
  }


  getEnvironmentConfig(){
    let layouts = this.visibleOverlays.map((overlay)=>{
      return overlay.asSimpleObject();
    });
    return {layouts: layouts};
  }

  setEnvironmentConfig(config){
    let overlays = this.overlays.asArray();
    //.filter((overlay)=>{
    //       return !overlay.isFloorLayout();
    //     });

    overlays.forEach((overlay)=>{
      //&& !this.isInVisibleOverlays(overlay)
      if (!config.layouts.includes(overlay.getId())){
        overlay.isVisible = false;
        overlay.setOpacity(0);
        overlay.hide();
        this.removeFromVisibleLayouts(overlay);
        this._changedInVisibleOverlays$.next({[overlay.getId()] : false})
      }
    });
    config.layouts.forEach((_overlay)=>{
      let overlay = this.getInOverlayList(overlays, _overlay.id);
      if (overlay){
        overlay.getEntityIds().forEach((value, key) => {
          this.view.removeOverlay(value);
        });
        
        console.log('Setting overlay config', _overlay);
        overlay.isVisible = true;
        overlay.setOpacity(_overlay.opacity);
        overlay.rotation = _overlay.rotation;
        
        // If we've saved left_top_lat, we'll have saved the rest too
        if (_overlay.left_top_lat) {
          overlay.left_top_lat = _overlay.left_top_lat;
          overlay.left_top_lon = _overlay.left_top_lon;
          overlay.right_bottom_lat = _overlay.right_bottom_lat;
          overlay.right_bottom_lon = _overlay.right_bottom_lon;
        }

        overlay.drawFromImageUrl(overlay.imageUrl, [
          overlay.left_top_lat,
          overlay.left_top_lon,
          overlay.right_bottom_lat,
          overlay.right_bottom_lon,
        ]);

        this.addToVisibleLayouts(overlay);
        this._changedInVisibleOverlays$.next({[_overlay.id]: true});
      }
    })
  }

  private getInOverlayList(overlays:Overlay[], id){
    for (let i = 0; i < overlays.length; i++){
      console.log('overlays[i].getId()', overlays[i].getId());
      console.log('id', id);
      if (overlays[i].getId() == id){
        return overlays[i];
      }
    }
    return null;
  }

}
